/**
 * Ход кампаний на карточке уровня — массив и хозяйство — из `/season/picture`.
 *
 * ПОЧЕМУ ЭТО НОВЫЙ НАБОР, А НЕ `obligations-<цикл>.json`. Карточки прототипа
 * брали журнал обязательств ЦЕЛИКОМ и складывали его в браузере: взять строки
 * цикла, отфильтровать по контурам массива (или по хозяйству) и посчитать
 * «отчитано / подтверждено». Канон такого журнала не отдаёт и отдавать не
 * должен — на районных объёмах это обход корпуса ради четырёх чисел. Сервер
 * уже умеет отвечать теми же четырьмя числами по любому срезу
 * (`/season/picture?cycle=…&massif_id=…`), поэтому экран получает ГОТОВЫЙ
 * агрегат, а не сырьё для собственной свёртки (правило переноса: клиентская
 * свёртка журнала на api-маршрутах запрещена).
 *
 * ПОЧЕМУ ЦИКЛЫ НЕ ЗАШИТЫ. Прототип жил ровно двумя циклами с постоянными
 * идентификаторами строк генератора (`cycle-cotton-2026`). У канона цикл — это
 * ПАРА «метка сезона × культура» (`2026:cotton`), и пара приезжает списком
 * (`/season/cycles`). Зашитые имена прототипа не сошлись бы ни с одним циклом
 * канона, и карточка молча показала бы «кампаний нет» — то есть соврала бы
 * отсутствием вместо отказа.
 *
 * ЦЕНА. Один запрос на цикл среза: сводка считается ПО ОДНОМУ циклу, и
 * объединять их в один ответ сервер не умеет. Циклов у района единицы, они
 * уходят параллельно, а памятью запроса (`once`) соседние карточки одного
 * контекста делят уже полученное.
 *
 * ПОЧЕМУ ВОДА ЕДЕТ ОТДЕЛЬНО ОТ ЦИКЛОВ. Канон осями сезона и культуры водный
 * баланс НЕ режет: у заявки на воду метки сезона нет (`SeasonAxes.water_scope`
 * передаёт только участника, массив и кампанию), и сервер сам говорит об этом
 * оговоркой «сезон к воде не применён». Значит по любому циклу приезжает ОДИН
 * И ТОТ ЖЕ баланс среза. Положить его в каждую строку цикла — значит дать
 * экрану сложить одно и то же число N раз и напечатать N-кратную заявку.
 * Поэтому вода спрашивается РОВНО ОДИН раз, запросом без оси цикла, и лежит
 * отдельным полем набора: сложить нечего, потому что слагаемое одно.
 *
 * ЧЕГО ЗДЕСЬ НЕТ. «Молчащих» и объёмы воды сервер отдаёт на носителе
 * `Measured` и десятичной строкой: пустое значение означает «не измерено», и
 * ноль вместо него запрещён (инвариант 5). Такие поля остаются `undefined`, а
 * экран обязан сказать «нет данных» словами. Класс доказательства «молчащих»
 * (`epistemic`) едет рядом с числом: синтетика прототипа и официальный счёт
 * на карточке обязаны отличаться подписью, а не догадкой читателя.
 */

import {
  api,
  type ActorDetail,
  type ActorRow,
  type QueryParams,
  type Schemas,
  type SeasonPicture,
} from '../../../api/client';
import type { ContourMeta, Farm } from '../../data/types';
import { type DatasetAdapter, once, paramsKey } from './adapter';
import { decimal, NO_CENTER, pageThrough } from './dataset';
import { defineMessages } from '../../../i18n/define';
import { messageRecord } from '../../lib/messageRecord';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  official: {
    id: 'data.epistemic.official',
    defaultMessage: 'официальные строки',
    description: 'Класс данных словами в подписи карточки уровня: строки из официальных источников (строчная фраза)',
  },
  derived: {
    id: 'data.epistemic.derived',
    defaultMessage: 'производные строки',
    description: 'Класс данных словами в подписи карточки уровня: строки, вычисленные из источников (строчная фраза)',
  },
  hypothesis: {
    id: 'data.epistemic.hypothesis',
    defaultMessage: 'гипотеза',
    description: 'Класс данных словами: значение — предположение, не факт (строчная фраза)',
  },
  syntheticDemo: {
    id: 'data.epistemic.syntheticDemo',
    defaultMessage: 'синтетика демонстрации',
    description: 'Класс данных словами: демонстрационные данные, не факт (строчная фраза)',
  },
  farmNo: {
    id: 'data.cards.farmNo',
    defaultMessage: 'Хозяйство {id}',
    description: 'Название хозяйства в списке массива, когда сервер имя не назвал; {id} — номер хозяйства',
  },
});


/** Уровень карточки. Третьего у этого набора нет: контур считается иначе. */
export type ScopeLevel = 'massif' | 'farm';

const PREFIX = 'scope-progress-';
const SUFFIX = '.json';

/** Параметр области, которым канон режет сводку под этот уровень. */
const SCOPE_PARAM: Record<ScopeLevel, string> = {
  massif: 'massif_id',
  farm: 'actor_id',
};

/** Имя набора для карточки уровня. Экран называет НАБОР, а не адрес. */
export function scopeProgressSet(level: ScopeLevel, id: string): string {
  return `${PREFIX}${level}-${id}${SUFFIX}`;
}

/** Разбор имени обратно. `null` — имя не этого адаптера. */
export function parseScopeSet(name: string): { level: ScopeLevel; id: string } | null {
  if (!name.startsWith(PREFIX) || !name.endsWith(SUFFIX)) return null;
  const body = name.slice(PREFIX.length, name.length - SUFFIX.length);
  const cut = body.indexOf('-');
  if (cut <= 0) return null;
  const level = body.slice(0, cut);
  const id = body.slice(cut + 1);
  if (level !== 'massif' && level !== 'farm') return null;
  if (!id) return null;
  return { level, id };
}

/** Обязательства среза в одном цикле — ровно те числа, что рисует `DoneSplit`. */
export interface ScopeObligations {
  total: number;
  reported: number;
  verified: number;
  /** «Молчащих» сервер МЕРЯЕТ; не измерено — `undefined`, а не ноль. */
  silent: number | undefined;
  /**
   * Класс доказательства «молчащих»: наихудший класс входных строк сервера.
   * `undefined` — строк не было вовсе (`rows == 0`), и класса у величины не
   * существует. Это НЕ то же самое, что `silent === undefined` («складывать
   * нечего»), и подменять одно другим нельзя.
   */
  silentEpistemic: Schemas['EpistemicStatus'] | undefined;
  /** Сколько строк сервер видел, считая молчание. Ноль — считать было не из чего. */
  silentRows: number;
}

/**
 * Вода среза: числа сервера и его же оговорка о подаче.
 *
 * Поцикловой воды у канона нет (см. шапку файла), поэтому у набора она одна на
 * весь срез и в строку цикла не кладётся.
 */
export interface ScopeWater {
  requests: number;
  requested_m3: number | undefined;
  delivered_m3: number | undefined;
  /**
   * Оговорка сервера о подаче (`water_note`). Едет рядом с числами всегда: на
   * демонстрационном срезе именно она объясняет, откуда подача взялась.
   */
  note: string;
}

export interface ScopeCycleProgress {
  /** Ключ цикла канона — тот же, что у строк `cycles.json`. */
  cycle: string;
  obligations: ScopeObligations;
}

export interface ScopeProgressFile {
  cycles: ScopeCycleProgress[];
  /**
   * Баланс воды ВСЕГО среза, не цикла: одно значение, а не слагаемое. Экран
   * печатает его как есть — складывать по циклам нечего.
   */
  water: ScopeWater;
}

/**
 * Класс доказательства словами. Клиент класс НЕ повышает и не переименовывает:
 * синтетика прототипа обязана читаться синтетикой и на карточке уровня.
 */
export const EPISTEMIC_WORD: Record<Schemas['EpistemicStatus'], string> = messageRecord({
  official: m.official,
  derived: m.derived,
  hypothesis: m.hypothesis,
  synthetic_demo: m.syntheticDemo,
});

/** Порядок классов канона: от худшего к лучшему (`core.epistemic_order`). */
const EPISTEMIC_ORDER: readonly Schemas['EpistemicStatus'][] = [
  'synthetic_demo',
  'hypothesis',
  'derived',
  'official',
];

/**
 * Класс суммы по циклам — НАИХУДШИЙ класс слагаемых, как считает сервер.
 * `undefined` остаётся, только если класса не было ни у одного слагаемого:
 * «строк не было вовсе» нельзя выдать за официальный счёт.
 */
export function worstEpistemic(
  values: readonly (Schemas['EpistemicStatus'] | undefined)[],
): Schemas['EpistemicStatus'] | undefined {
  let worst: Schemas['EpistemicStatus'] | undefined;
  for (const value of values) {
    if (value === undefined) continue;
    if (worst === undefined || EPISTEMIC_ORDER.indexOf(value) < EPISTEMIC_ORDER.indexOf(worst)) {
      worst = value;
    }
  }
  return worst;
}

/** Один ответ сводки → строка хода по циклу. Воду эта строка не несёт. */
export function toScopeCycle(cycle: string, picture: SeasonPicture): ScopeCycleProgress {
  return {
    cycle,
    obligations: {
      total: picture.obligations.total,
      reported: picture.obligations.reported,
      verified: picture.obligations.verified,
      silent: picture.no_report.value ?? undefined,
      silentEpistemic: picture.no_report.epistemic ?? undefined,
      silentRows: picture.no_report.rows,
    },
  };
}

/** Тот же ответ → баланс воды среза. Берётся из ответа БЕЗ оси цикла. */
export function toScopeWater(picture: SeasonPicture): ScopeWater {
  return {
    requests: picture.water.requests,
    requested_m3: decimal(picture.water.requested_volume_m3) ?? undefined,
    delivered_m3: decimal(picture.water.delivered_volume_m3) ?? undefined,
    note: picture.water_note,
  };
}

/* ------------------------------------------------ земля хозяйства и массива */

/**
 * ПОЧЕМУ ЗЕМЛЯ КАРТОЧКИ — ОТДЕЛЬНЫЙ НАБОР, А НЕ ФИЛЬТР `contours.meta.json`.
 *
 * Реестр контуров называет у строки ОДНОГО землепользователя (первого), а у
 * контура их бывает до сотни (`land_users_count`). Фильтр реестра по
 * `farm_id` давал карточке хозяйства только контуры, где оно названо первым
 * (у хозяйства 22 — 326), тогда как кабинет хозяйства (`/my/fields`) и сам
 * канон (`/actors/{id}`) считают ВСЕ его землепользования (663). Два экрана
 * одного хозяйства расходились вдвое. Теперь карточка берёт ту же выборку,
 * что и кабинет: землепользования субъекта из `/actors/{id}`.
 *
 * То же с массивом: список хозяйств массива из первых землепользователей
 * реестра терял тех, кто в массиве делит землю не первым. Канон отвечает на
 * этот вопрос сам — `/actors?massif_id=` (хозяйства с любой долей земли в
 * массиве), и счёт карточки берётся оттуда.
 */
const LAND_PREFIX = 'farm-land-';
const MASSIF_FARMS_PREFIX = 'massif-farms-';

export const farmLandSet = (id: string) => `${LAND_PREFIX}${id}${SUFFIX}`;
export const massifFarmsSet = (id: string) => `${MASSIF_FARMS_PREFIX}${id}${SUFFIX}`;

function idOf(name: string, prefix: string): number | null {
  if (!name.startsWith(prefix) || !name.endsWith(SUFFIX)) return null;
  const id = Number(name.slice(prefix.length, name.length - SUFFIX.length));
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * Землепользования субъекта → контуры карточки. Контур с несколькими частями
 * у одного хозяйства — одна строка: карточка считает КОНТУРЫ, а не части.
 */
export function toFarmLand(detail: ActorDetail): ContourMeta[] {
  const byContour = new Map<number, ContourMeta>();
  for (const use of detail.land_uses) {
    const known = byContour.get(use.contour_id);
    // Площадь контура — ОБЩАЯ площадь землепользования (как «Земля хозяйства»
    // реестра, `TOTAL_LAND`), пашня — только когда общей нет. Раньше первой
    // бралась пашня, и карточка печатала рядом две разные «земли»: 48,55 га
    // общей площади и 44,95 га пашни (agroai-6al.2).
    const area = use.total_microha ?? use.arable_microha;
    if (known) {
      if (area !== null && area !== undefined) known.area_ha += area / 1_000_000;
      continue;
    }
    byContour.set(use.contour_id, {
      id: String(use.contour_id),
      no: use.contour_no,
      massif_id: String(use.massif.id),
      farm_id: String(detail.id),
      area_ha: area === null || area === undefined ? 0 : area / 1_000_000,
      area_stated: area !== null && area !== undefined,
      // Категория земли и размещение культуры у землепользования не
      // объявлены — «неизвестно», а не «прочее».
      category: 'unknown',
      crop_2026: null,
      crop_2025w: null,
      // Наличие полигона землепользование не называет: «неизвестно», а не
      // «полигона нет».
      geometry: 'unknown',
      center: NO_CENTER,
    });
  }
  return [...byContour.values()];
}

/** Хозяйство из списка `/actors` — та же форма, что у `farms.json` на каноне. */
export function toMassifFarm(row: ActorRow, massifId: string): Farm {
  const area = row.total_microha;
  return {
    id: String(row.id),
    name: row.name ?? tr(m.farmNo, { id: row.id }),
    massif_ids: [massifId],
    direction: row.directions[0]?.raw_label ?? '',
    area_ha: area === null || area === undefined ? 0 : area / 1_000_000,
    contours: row.contours,
    head: '',
  };
}

export const cardsAdapter: DatasetAdapter = {
  domain: 'cards',

  match(name) {
    return (
      parseScopeSet(name) !== null ||
      idOf(name, LAND_PREFIX) !== null ||
      idOf(name, MASSIF_FARMS_PREFIX) !== null
    );
  },

  async load(name, params: QueryParams) {
    const actorId = idOf(name, LAND_PREFIX);
    if (actorId !== null) {
      const detail = await once(`actor|${actorId}|${paramsKey(params)}`, () =>
        api.actor(actorId, params),
      );
      return toFarmLand(detail);
    }
    const massifId = idOf(name, MASSIF_FARMS_PREFIX);
    if (massifId !== null) {
      const list = await once(`massif-actors|${massifId}|${paramsKey(params)}`, () =>
        pageThrough<ActorRow>((limit, offset) =>
          api.actors({ ...params, massif_id: massifId, limit, offset }),
        ),
      );
      return list.items.map((row) => toMassifFarm(row, String(massifId)));
    }

    const scope = parseScopeSet(name);
    // `match` уже сказал «имя моё», поэтому сюда попадает только разборное имя.
    // i18n-ignore: ошибка для разработчика
    if (!scope) throw new Error(`имя набора «${name}» не разбирается`);

    const cycles = await once(`season-cycles|${paramsKey(params)}`, () =>
      api.seasonCycles(params),
    );

    // Разрезы сводки карточке не нужны: она показывает ИТОГ среза, а разбивку
    // по массивам и хозяйствам рисуют другие экраны. Просить их здесь значило
    // бы платить за то, что никто не прочитает.
    const base: QueryParams = {
      ...params,
      [SCOPE_PARAM[scope.level]]: scope.id,
      summary: false,
      weeks: 0,
    };

    const ask = (query: QueryParams) =>
      once(`picture|${paramsKey(query)}`, () => api.seasonPicture(query));

    const [rows, whole] = await Promise.all([
      Promise.all(
        cycles.items.map(async (row) => {
          const picture = await ask({ ...base, cycle: row.cycle });
          return toScopeCycle(row.cycle, picture);
        }),
      ),
      // Вода — один запрос БЕЗ оси цикла: канон её этой осью не режет, и
      // поцикловые ответы вернули бы один и тот же баланс по разу на цикл.
      ask(base),
    ]);

    return { cycles: rows, water: toScopeWater(whole) } satisfies ScopeProgressFile;
  },
};
